import React, { useState } from 'react';
import { useCookies } from '../contexts/CookieContext';
import { 
  X, 
  Shield, 
  BarChart3, 
  Target, 
  Settings,
  Check,
  AlertCircle,
  Info
} from 'lucide-react';

const CookiePreferences = () => {
  const {
    showPreferences,
    setShowPreferences,
    cookiePreferences,
    savePreferences,
    cookieTypes
  } = useCookies();

  const [localPreferences, setLocalPreferences] = useState(cookiePreferences);

  if (!showPreferences) return null;

  // Icônes pour chaque type de cookie
  const cookieIcons = {
    essential: Shield,
    analytics: BarChart3,
    marketing: Target,
    preferences: Settings
  };

  // Couleurs pour chaque type
  const cookieColors = {
    essential: 'text-green-600 bg-green-100',
    analytics: 'text-blue-600 bg-blue-100',
    marketing: 'text-purple-600 bg-purple-100',
    preferences: 'text-orange-600 bg-orange-100'
  };

  const handleToggle = (cookieType) => {
    if (localPreferences[cookieType].required) return;

    setLocalPreferences(prev => ({
      ...prev,
      [cookieType]: {
        ...prev[cookieType],
        enabled: !prev[cookieType].enabled
      }
    }));
  };

  const handleSave = () => {
    savePreferences(localPreferences);
  };

  const acceptAll = () => {
    const allEnabled = { ...localPreferences };
    Object.keys(allEnabled).forEach(key => {
      if (!allEnabled[key].required) {
        allEnabled[key].enabled = true;
      }
    });
    setLocalPreferences(allEnabled);
  };

  const rejectAll = () => {
    const onlyEssential = { ...localPreferences };
    Object.keys(onlyEssential).forEach(key => {
      if (!onlyEssential[key].required) {
        onlyEssential[key].enabled = false;
      }
    });
    setLocalPreferences(onlyEssential);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🍪 Préférences des cookies
              </h2>
              <p className="text-gray-600 mt-1">
                Personnalisez vos préférences de cookies selon vos besoins
              </p>
            </div>
            <button
              onClick={() => setShowPreferences(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Information importante */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info size={20} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900">Information importante</h3>
                <p className="text-blue-800 text-sm mt-1">
                  Les cookies essentiels sont nécessaires au bon fonctionnement du site et ne peuvent pas être désactivés. 
                  Vous pouvez configurer les autres types selon vos préférences.
                </p>
              </div>
            </div>
          </div>

          {/* Types de cookies */}
          <div className="space-y-4 mb-6">
            {Object.entries(localPreferences).map(([key, cookie]) => {
              const IconComponent = cookieIcons[key];
              const colorClass = cookieColors[key];
              
              return (
                <div key={key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
                        <IconComponent size={20} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900">{cookie.name}</h3>
                          {cookie.required && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <AlertCircle size={12} className="mr-1" />
                              Obligatoire
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mt-1">{cookie.description}</p>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <div className="flex-shrink-0 ml-4">
                      <button
                        onClick={() => handleToggle(key)}
                        disabled={cookie.required}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 ${
                          cookie.enabled 
                            ? 'bg-brand-primary' 
                            : 'bg-gray-200'
                        } ${
                          cookie.required ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            cookie.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions rapides */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Actions rapides</h3>
            <div className="flex space-x-3">
              <button
                onClick={acceptAll}
                className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-medium"
              >
                Tout accepter
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
              >
                Refuser les optionnels
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={() => setShowPreferences(false)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-bold flex items-center justify-center"
            >
              <Check size={16} className="mr-2" />
              Enregistrer mes préférences
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mt-3 text-center">
            Vos préférences seront enregistrées et appliquées immédiatement
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences;